<script setup lang="ts">
  import ChevronRight from "@/components/icons/ChevronRight.vue";

  type Crumb = { label: string };

  type Props = {
    items: Crumb[];
    // Optional label appended at the end as the (unclickable) current step.
    current?: string;
  };

  const props = defineProps<Props>();
  const emit = defineEmits<{ select: [index: number] }>();
</script>

<template>
  <nav v-if="props.items.length || props.current" :class="$style.base" aria-label="breadcrumbs">
    <template v-for="(item, index) in props.items" :key="index">
      <button type="button" :class="$style.crumb" @click="emit('select', index)">
        {{ item.label }}
      </button>
      <ChevronRight :size="14" :class="$style.separator" />
    </template>
    <span v-if="props.current" :class="$style.current">{{ props.current }}</span>
  </nav>
</template>

<style lang="scss" module>
  .base {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;

    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  // Cap label width and ellipsize — a long report name shouldn't push the
  // breadcrumb chain off the page on mobile.
  .crumb,
  .current {
    max-width: 12rem;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .crumb {
    padding: 2px 6px;

    background: transparent;
    border: none;
    border-radius: var(--radius, 6px);
    color: var(--muted-foreground);
    cursor: pointer;

    font: inherit;

    &:hover {
      color: var(--foreground);
      background-color: var(--accent);
    }
  }

  .separator {
    flex-shrink: 0;
    color: var(--muted-foreground);
  }

  .current {
    padding: 2px 6px;

    color: var(--foreground);
    font-weight: 600;
  }
</style>
